import { useEffect, useState } from "react";
import styled from "styled-components";
import Theme from "./Theme";
import GlobalStyle from "./GlobalStyle";
import { LinkingFlowActionEnum } from "../config/types";
import { fetchWithoutAuth } from "../utils/apiClient";
import {
  IS_SELF_HOSTED,
  loadRuntimeConfig,
  resolveSelfHostedApiBaseURL,
} from "../utils/runtimeConfig";
import { listenToParent } from "../utils/postMessageUtils";
import MergeLinkModalRoot from "./MergeLinkModalRoot";
import ErrorBoundary from "../flow/ErrorBoundary";
import TryAgainLaterScreen from "./interstitials/TryAgainLaterScreen";
import { logLinkExecutionRecord } from "../utils/fetchUtils";
import useTelemetryIdentity from "./hooks/useTelemetryIdentity";
import LoadingScreen from "./interstitials/LoadingScreen";

const CenteredContainer = styled.div`
  width: 100%;
  height: 100%;
  background: rgba(8, 8, 8, 0.75);
  display: flex;
`;

const App = () => {
  const validateApiBaseURL = (apiBaseURLUnvalidated: string) => {
    const parsedUrl = new URL(apiBaseURLUnvalidated);
    // Single tenants served from a customer-owned apex do not match the merge.dev
    // suffix. Listed as an exact host, not as .factorymerge.com: that apex belongs to
    // the customer, so a suffix match would trust every subdomain of it.
    let validDomains = [
      ".merge.dev",
      "ah-api-factory-usw2.factorymerge.com",
      "connectors-api.factory.ai",
    ];
    let validProtocols = ["https"];
    if (process.env.NODE_ENV === "development" || process.env.REACT_APP_MERGE_ENV === "LOCAL") {
      validDomains.push("localhost");
      validDomains.push("127.0.0.1");
      validDomains.push("ah-api-develop.merge.dev");

      validProtocols.push("http");
    }

    let domainValid = false;
    for (const domain of validDomains) {
      if (parsedUrl.hostname.endsWith(domain)) {
        domainValid = true;
        break;
      }
    }
    if (!domainValid) {
      console.error(`Invalid apiBaseURL, host is not an allowed API host: ${parsedUrl.hostname}`);
      return false;
    }

    let pathValid = false;
    if (parsedUrl.pathname === "/api") {
      pathValid = true;
    }
    if (!pathValid) {
      console.error(`Invalid apiBaseURL, path must be /api: ${parsedUrl.pathname}`);
      return false;
    }

    let protocolValid = false;
    for (const protocol of validProtocols) {
      if (parsedUrl.protocol.startsWith(protocol)) {
        protocolValid = true;
        break;
      }
    }
    if (!protocolValid) {
      console.error(`Invalid apiBaseUrl, must be https: ${parsedUrl.protocol}`);
      return false;
    }

    return true;
  };

  const [linkToken, setLinkToken] = useState<string | undefined>();
  const [apiBaseURL, setApiBaseURL] = useState<string>();
  const [forAdmin, setForAdmin] = useState<boolean>(false);
  const [isWaitingForReopenConfirmation, setIsWaitingForReopenConfirmation] =
    useState<boolean>(false);
  const [isLinkOpen, setIsLinkOpen] = useState<boolean>(false);
  const [initialPageResponseBody, setInitialPageResponseBody] = useState<any>();
  const [isLinkTAndCHidden, setIsLinkTAndCHidden] = useState<boolean>(false);
  const [shouldHideMergeLogo, setShouldHideMergeLogo] = useState<boolean>(false);
  const [shouldShowPoweredByMerge, setShouldShowPoweredByMerge] = useState<boolean>(false);

  useEffect(() => {
    if (isLinkOpen) {
      if (apiBaseURL && linkToken) {
        logLinkExecutionRecord({
          apiBaseURL,
          linkToken,
          eventName: LinkingFlowActionEnum.LINKING_FLOW_OPENED,
          onSuccess: () => {},
        });
      }
    }
  }, [isLinkOpen]);

  // Take the link token and settings from the page embedding Link, then ask it for
  // them. Registered once, so the handler sees this first render's state.
  useEffect(() => {
    const stopListening = listenToParent((event) => {
      if (event.data.messageType === "SEND_INFO_TO_IFRAME") {
        if (event.data.linkToken && event.data.linkToken !== linkToken) {
          setLinkToken(event.data.linkToken);
          setForAdmin(event.data.forAdmin);
        }
        const isLinkOpenChanged =
          event.data.isLinkOpen !== undefined && event.data.isLinkOpen !== isLinkOpen;
        if (isLinkOpenChanged && !IS_SELF_HOSTED) {
          setIsLinkOpen(event.data.isLinkOpen);
        }

        if (IS_SELF_HOSTED) {
          // Self-hosted Link only ever uses the API in its config.json. It opens
          // only once that's loaded, so the first open doesn't flash
          // TryAgainLaterScreen or skip logging LINKING_FLOW_OPENED.
          const { apiBaseURL: requested, isLinkOpen: requestedIsLinkOpen } = event.data;
          loadRuntimeConfig().then((config) => {
            const resolved = resolveSelfHostedApiBaseURL(requested, config);
            if ("error" in resolved) {
              console.error(resolved.error);
            } else {
              setApiBaseURL(resolved.apiBaseURL);
            }
            if (isLinkOpenChanged) {
              setIsLinkOpen(requestedIsLinkOpen);
            }
          });
        } else if (
          event.data.apiBaseURL &&
          event.data.apiBaseURL !== apiBaseURL &&
          validateApiBaseURL(event.data.apiBaseURL)
        ) {
          setApiBaseURL(event.data.apiBaseURL);
        }

        setIsWaitingForReopenConfirmation(false);

        if (process.env.NODE_ENV === "development") {
          setInitialPageResponseBody(event.data.initialPageResponseBody);
        }
      }
    });
    // eslint-disable-next-line no-restricted-globals
    parent.postMessage({ messageType: "LINKING_FLOW_READY_FOR_LINK_TOKEN" }, "*");
    return stopListening;
  }, []);

  useEffect(() => {
    if (linkToken != null && apiBaseURL) {
      fetchWithoutAuth({
        apiBaseURL,
        path: `connectors/link/eligibility-check`,
        method: "POST",
        body: { link_token: linkToken },
        onResponse: (data) => {
          setIsLinkTAndCHidden(data?.is_link_tandc_hidden);
          setShouldHideMergeLogo(data?.should_hide_merge_logo);
          setShouldShowPoweredByMerge(data?.should_show_powered_by_merge);
        },
        onError: () => {},
      });
    }
  }, [linkToken, apiBaseURL]);

  useTelemetryIdentity({ linkToken, apiBaseURL });

  return linkToken != null && apiBaseURL != null ? (
    <CenteredContainer>
      <Theme>
        <ErrorBoundary
          apiBaseURL={apiBaseURL}
          linkToken={linkToken}
          fallback={<TryAgainLaterScreen />}
        >
          {isWaitingForReopenConfirmation ? (
            <LoadingScreen />
          ) : (
            <MergeLinkModalRoot
              onLinkClose={() => setIsLinkOpen(false)}
              isLinkTAndCHidden={isLinkTAndCHidden}
              apiBaseURL={apiBaseURL}
              linkToken={linkToken}
              forAdmin={forAdmin}
              setLinkToken={setLinkToken}
              setIsWaitingForReopenConfirmation={setIsWaitingForReopenConfirmation}
              isLinkOpen={isLinkOpen}
              initialPageResponseBody={initialPageResponseBody}
              shouldHideMergeLogo={shouldHideMergeLogo}
              shouldShowPoweredByMerge={shouldShowPoweredByMerge}
            />
          )}
          <GlobalStyle />
        </ErrorBoundary>
      </Theme>
    </CenteredContainer>
  ) : (
    <CenteredContainer>
      <Theme>{isLinkOpen && <TryAgainLaterScreen />}</Theme>
    </CenteredContainer>
  );
};

export default App;
